/**
 * RendererFactory — selects the renderer substrate.
 *
 * **Phase H10 Arc λ.7 (2026-05-09): default flipped to 'pixi', no escape
 * hatch.** Both `?renderer=phaser` and `?renderer=pixi` (and no flag at all)
 * resolve to 'pixi'. The dual-path getter is preserved for one cleanup
 * window — Arc λ.10 deletes the 12 PhaserXxx adapter files + factory
 * registration plumbing, after which the substrate type can collapse
 * to a single Pixi-only path.
 *
 * Test-only `__resetRendererModeForTesting()` still toggles a cached
 * value but the cache only ever holds 'pixi' in production code. Tests
 * that simulate Phaser mode by stuffing the URL query are now no-ops at
 * the renderer-mode level — they still produce 'pixi'.
 */

export type RendererMode = 'phaser' | 'pixi';

let cachedMode: RendererMode | null = null;

export function getRendererMode(): RendererMode {
  if (cachedMode !== null) return cachedMode;
  cachedMode = 'pixi';
  return cachedMode;
}

/**
 * Test-only escape hatch — resets the cached mode so a test can re-read
 * the resolved value after simulated environment changes. NEVER call
 * from production code.
 */
export function __resetRendererModeForTesting(): void {
  cachedMode = null;
}

/**
 * Bind the active CameraSystem's ICamera as the source-of-truth for the
 * Pixi overlay's world<->screen transform.
 *
 * Phase H10 Arc λ.9 Stage C — was `bindPhaserCameraForPixi`, wrapped
 * `scene.cameras.main` in a PhaserCamera adapter. After CameraManager
 * migrated to SubstrateCamera, that wrapper bound to a no-longer-mutated
 * Phaser camera (still alive at scene.cameras.main but unused by gameplay
 * scroll/zoom mutations). The result was BUG B-001 — VFX rendering with
 * (0, 0) scroll regardless of where the camera "actually" was, putting
 * world-space content at a fixed offset matching the camera's intended
 * scroll position.
 *
 * Fix: bind directly to `cameraSystem.getManager().getICamera()` which
 * is the SubstrateCamera that CameraManager mutates. ICamera surface is
 * the same shape; Pixi overlay reads scrollX/scrollY/zoom each frame.
 *
 * The `scene` parameter is now unused but retained so callers don't
 * need to update — the helper retires entirely in Arc λ.9 Stage D when
 * GameHost owns the canvas + Pixi.Application directly.
 */
export function bindPhaserCameraForPixi(_scene?: unknown): void {
  void import('./PixiOverlayHost').then(({ PixiOverlayHost }) => {
    void Promise.all([
      PixiOverlayHost.ensureReady(),
      import('../core/ServiceRegistry'),
    ]).then(([, { ServiceRegistry }]) => {
      const cam = ServiceRegistry.getInstance()
        .get('CameraSystem', { optional: true })
        ?.getManager()
        ?.getICamera();
      if (cam) {
        PixiOverlayHost.bindCameraSource(cam);
      } else {
        // CameraSystem not registered yet — Pixi overlay will read stale
        // (0, 0) scroll until rebind. Race-window during boot; should
        // not persist past first frame post-GameScene.create().
      }
    });
  });
}
